import React from 'react';
import { SignatureInvalidationDialog } from '../SignatureInvalidationDialog';
import { AttestationDialog } from '../signing/AttestationDialog';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import type { SigningAttestation } from '../signing/attestations';
import type { TaskSignatureResponse } from '../../services/api/types.gen';

export interface VisitSigningDialogsProps {
  // Signature invalidation (OASIS save or unlock)
  showConfirmDialog: boolean;
  isUnlockAction: boolean;
  signatures: TaskSignatureResponse[];
  invalidationLoading: boolean;
  editButtonLabel: string;
  onConfirmSave: () => void;
  onUnlockConfirm: () => void;
  onCancelSave: () => void;

  // Sign attestation
  signAttestationOpen: boolean;
  signRequiresESignature: boolean;
  visitAttestation: SigningAttestation | null;
  signerName: string | null;
  signerEmail: string | null;
  signingLoading: boolean;
  signButtonLabel: string;
  patientId: string;
  /** The visit being signed; seeds the signature date from its visit date. */
  visitId: string;
  onSignConfirm: (
    credential: ESignatureCredentialInput,
    signatureDate?: SignatureDateInput
  ) => Promise<string | null>;
  onSignCancel: () => void;

  // Approve attestation
  approveAttestationOpen: boolean;
  approvingLoading: boolean;
  onApproveConfirm: (
    credential: ESignatureCredentialInput
  ) => Promise<string | null>;
  onApproveCancel: () => void;

  /** Bypass ceremony dialog node from useSignatureBypassAction. */
  bypassDialog: React.ReactNode;
}

/**
 * Signing / attestation / invalidation dialog cluster for a visit chart.
 * Invalidation confirm stays wired here so the panel can keep OASIS save and
 * unlock paths in one place without inlining the dialog JSX.
 */
export const VisitSigningDialogs: React.FC<VisitSigningDialogsProps> = ({
  showConfirmDialog,
  isUnlockAction,
  signatures,
  invalidationLoading,
  editButtonLabel,
  onConfirmSave,
  onUnlockConfirm,
  onCancelSave,
  signAttestationOpen,
  signRequiresESignature,
  visitAttestation,
  signerName,
  signerEmail,
  signingLoading,
  signButtonLabel,
  patientId,
  visitId,
  onSignConfirm,
  onSignCancel,
  approveAttestationOpen,
  approvingLoading,
  onApproveConfirm,
  onApproveCancel,
  bypassDialog,
}) => (
  <>
    <SignatureInvalidationDialog
      open={showConfirmDialog}
      onConfirm={isUnlockAction ? onUnlockConfirm : onConfirmSave}
      onCancel={onCancelSave}
      signatures={signatures}
      isLoading={invalidationLoading}
      actionLabel={isUnlockAction ? editButtonLabel : undefined}
    />

    <AttestationDialog
      open={signAttestationOpen}
      requiresCredential={signRequiresESignature}
      attestation={visitAttestation}
      signerName={signerName}
      signerEmail={signerEmail}
      isLoading={signingLoading}
      actionLabel={signButtonLabel}
      // Clinician lane only -- see the prop's docs on AttestationDialog.
      showSignatureDate={signRequiresESignature}
      patientId={patientId}
      taskId={visitId}
      onConfirm={onSignConfirm}
      onCancel={onSignCancel}
    />

    <AttestationDialog
      open={approveAttestationOpen}
      requiresCredential={false}
      busyLabel='Approving…'
      attestation={visitAttestation}
      signerName={signerName}
      signerEmail={signerEmail}
      isLoading={approvingLoading}
      actionLabel='Approve & Complete'
      onConfirm={onApproveConfirm}
      onCancel={onApproveCancel}
    />

    {bypassDialog}
  </>
);
